import { Injectable, effect, signal } from '@angular/core';
import { DateTime, Settings } from 'luxon';
import { BehaviorSubject, map, mergeMap, tap } from 'rxjs';
import { OpenAiFileObject } from '../../../../../backend/src/core/board/board.gate';
import { ResourceName } from '../../../../../backend/src/core/resource-name';
import {
  AddUserListToWorkspaceRequest,
  DeleteOpenAiFileRequest,
  LeaveWorkspaceRequest,
  ResendEmailRequest,
  SendWeeklyReportRequest,
  UpdateHubSpotRequest,
  WorkspaceGate,
} from '../../../../../backend/src/core/workspace/workspace.gate';
import { StripeProductStatus, StripeProductType } from '../stripe/product.stripe';
import { TimezoneService } from '../timezone/timezone.service';
import { UserClerk } from '../user/user-clerk';
import { UserClerkCallBackendService } from '../user/user-clerk-call-backend.service';
import { LambdaService } from './../../shared/core/lambda.service';
import { EmailToken, HourlyTask, SalesCycle, Workspace } from './workspace';
import { WorkspaceService } from './workspace.service';

@Injectable({
  providedIn: 'root',
})
export class WorkspaceHolderService {
  private resourceName = ResourceName.workspace;

  currentWorkspace = signal<Workspace>({} as any);
  currentSalesCycle = signal<SalesCycle | undefined>(undefined);

  fileList = signal<OpenAiFileObject[]>([]);

  constructor(
    private timezoneService: TimezoneService,
    private lambdaService: LambdaService,
    private workspaceService: WorkspaceService,
    private userClerkCallBackendService: UserClerkCallBackendService
  ) {
    effect(
      () => {
        const workspace = this.currentWorkspace();

        if (localStorage.getItem(this.useLocalTimezoneKey)) {
          Settings.defaultZone = localStorage.getItem(this.useLocalTimezoneKey)!;
        } else {
          if (workspace.timezone) {
            Settings.defaultZone = workspace.timezone;
          }
        }
      },
      { allowSignalWrites: true }
    );

    // this.getChargingHistoryList$()
    //   .pipe(take(1))
    //   .subscribe((list) => {
    //     const workspace = this.currentWorkspace();

    //     if (workspace.publicSubData?.trialExpiredTimeStr) {
    //       const trialExpiredTime = DateTime.fromISO(workspace.publicSubData.trialExpiredTimeStr);
    //       const now = DateTime.local();

    //       if (trialExpiredTime < now) {
    //         return {
    //           isPaid: false,
    //           type: 'basic',
    //         };
    //       } else {
    //       }
    //     }
    //   });
  }

  subscriptionId = signal<string>('');

  paidStatus = signal<
    { status: StripeProductStatus; type?: StripeProductType; startTime?: DateTime; endTime?: DateTime } | undefined
  >(undefined);

  isLoadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private readonly useLocalTimezoneKey = 'useLocalTimezone';

  get timezoneOffset() {
    return this.timezoneService.getLocalShortOffset(DateTime.local());
  }

  sendWeeklyReport$(hourlyTask: HourlyTask) {
    const workspace = this.currentWorkspace();

    const request: SendWeeklyReportRequest = {
      workspaceId: workspace.id,
      hourlyTask: hourlyTask,
    };

    return this.lambdaService.run$(this.resourceName, WorkspaceGate.actions.sendWeeklyReport, request);
  }

  updateHourlyTaskList$() {
    const workspace = this.currentWorkspace();
    return this.lambdaService.run$(this.resourceName, WorkspaceGate.actions.updateHourlyTaskList, workspace).pipe(
      tap((updatedWorkspace) => {
        this.currentWorkspace.set(updatedWorkspace);
      })
    );
  }

  updateHubSpot$(hubSpotAccessToken: string) {
    const request: UpdateHubSpotRequest = {
      workspaceId: this.currentWorkspace().id,
      hubSpotAccessToken: hubSpotAccessToken,
    };
    return this.lambdaService.run$(this.resourceName, WorkspaceGate.actions.updateHubSpot, request);
  }

  inviteMemberList$(emailList: string[]) {
    return this.userClerkCallBackendService.getUserListByEmailList$(emailList).pipe(
      mergeMap((userList: UserClerk[]) => {
        const addUserListToWorkspaceUserList: any[] = emailList.map((email) => ({ email }));

        for (const email of emailList) {
          const user = userList.find((c) => c.emailAddresses?.find((c) => c.emailAddress === email));

          if (user) {
            const addUser = addUserListToWorkspaceUserList.find((c) => c.email === email);
            addUser.id = user.id;
          }
        }

        const request: AddUserListToWorkspaceRequest = {
          workspaceId: this.currentWorkspace().id,
          userList: addUserListToWorkspaceUserList,
        };
        return this.workspaceService.addUserListToWorkspace$(request);
      })
    );
  }

  updateCurrentWorkspace$() {
    return this.workspaceService.updateWorkspace$(this.currentWorkspace()).pipe(
      map((data) => {
        this.currentWorkspace.set(data);
      })
    );
  }

  updateCurrentWorkspaceInCache(workspace: Workspace) {
    this.currentWorkspace.set(workspace);
  }

  hasUseLocalTimezone() {
    return !!localStorage.getItem(this.useLocalTimezoneKey);
  }

  useLocalTimezone() {
    localStorage.setItem(this.useLocalTimezoneKey, Intl.DateTimeFormat().resolvedOptions().timeZone);

    window.location.reload();
  }

  useWorkspaceTimezone() {
    localStorage.removeItem(this.useLocalTimezoneKey);

    window.location.reload();
  }

  deleteOpenAiFile$(fileId: string) {
    const request: DeleteOpenAiFileRequest = {
      workspaceId: this.currentWorkspace().id,
      fileId,
    };

    return this.lambdaService.run$(this.resourceName, WorkspaceGate.actions.deleteOpenAiFile, request);
  }

  deleteCurrentWorkspace$() {
    return this.workspaceService.deleteWorkspaceById$(this.currentWorkspace()!.id);
  }

  leaveWorkspace$() {
    const request: LeaveWorkspaceRequest = {
      workspaceId: this.currentWorkspace().id,
    };
    return this.lambdaService.run$(this.resourceName, WorkspaceGate.actions.leaveWorkspace, request).pipe();
  }

  resendEmail$(emailToken: EmailToken) {
    const request: ResendEmailRequest = {
      email: emailToken.email,
      token: emailToken.token,
      workspaceId: this.currentWorkspace().id,
    };

    return this.lambdaService.run$(this.resourceName, WorkspaceGate.actions.resendEmail, request);
  }

  // _chargingHistoryListCached?: WorkspaceComboBackend[];

  // getChargingHistoryList$() {
  //   if (this._chargingHistoryListCached) {
  //     return of(this._chargingHistoryListCached);
  //   }

  //   return this.getWorkspaceItemListByItemType$(WorkspaceItemType.chargingHistory).pipe(
  //     tap((chargingItemList) => {
  //       this._chargingHistoryListCached = chargingItemList;
  //     })
  //   );
  // }

  // getWorkspaceItemListByItemType$(itemType: WorkspaceItemType) {
  //   const request: GetWorkspaceItemListByItemTypeRequest = {
  //     workspaceId: this.currentWorkspace().id,
  //     itemType,
  //   };

  //   return this.lambdaService
  //     .run$(this.resourceName, WorkspaceGate.actions.getWorkspaceItemListByItemType, request)
  //     .pipe(
  //       map((data) => {
  //         return data as WorkspaceComboBackend[];
  //       })
  //     );
  // }
}
